import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Paper, Typography, Dialog, DialogContent, Tooltip } from '@mui/material';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import UpcomingRoundedIcon from '@mui/icons-material/UpcomingRounded';
import AllInboxRoundedIcon from '@mui/icons-material/AllInboxRounded';
import EngineeringRoundedIcon from '@mui/icons-material/EngineeringRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import AssignmentReturnRoundedIcon from '@mui/icons-material/AssignmentReturnRounded';

import ProjectPopup from '../Project/ProjectPopup';
import * as actions from '../../redux/actions';
import { useSocket } from '../../contexts/SocketContext';
import api from '../../utils/functions/api';
import ExpensesTabToday from './ExpensesTabToday';
import ExpensesTabBacklog from './ExpensesTabBacklog';
import ExpensesTabFuture from './ExpensesTabFuture';
import ExpensesTabAllGroup from './ExpensesTabAllGroup';
import ExpensesTabLabor from './ExpensesTabLabor';
import ExpensesTabRecurringGenerated from './ExpensesTabRecurringGenerated';
import ExpensesTabCrl from './ExpensesTabCrl';
import ExpensesTabRefund from './ExpensesTabRefund';

// tab ค้างดำเนินการ/อนาคต/Archive/ทั้งหมด/ปฏิเสธ-ยกเลิก เคยใช้ component เดียวกัน (ExpensesTabPanel.js) ผ่าน
// prop tabKey แยกเป็นคนละไฟล์แล้ว (ตามที่ขอ เผื่อจะแก้ style/behavior เฉพาะ tab ในอนาคต) — map จาก tab key
// ไป component ที่ตรงกัน แทนที่จะ import มา switch ทีละ if
const TAB_COMPONENTS = {
  backlog: ExpensesTabBacklog,
  future: ExpensesTabFuture,
  all: ExpensesTabAllGroup,
  recurring: ExpensesTabRecurringGenerated,
  crl: ExpensesTabCrl,
  refund: ExpensesTabRefund,
};

const TABS = [
  {
    key: 'today',
    label: 'วันนี้',
    icon: TodayRoundedIcon,
    gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  },
  {
    key: 'backlog',
    label: 'ค้างดำเนินการ',
    icon: PendingActionsRoundedIcon,
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
  },
  {
    key: 'future',
    label: 'อนาคต',
    icon: UpcomingRoundedIcon,
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
  },
  {
    key: 'labor',
    label: 'ค่าแรง',
    icon: EngineeringRoundedIcon,
    gradient: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
  },
  {
    key: 'all',
    label: 'ทั้งหมด',
    icon: AllInboxRoundedIcon,
    gradient: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
  },
  {
    key: 'recurring',
    label: 'รายการเบิกประจำ',
    icon: EventRepeatRoundedIcon,
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
  },
  {
    key: 'crl',
    label: 'เบิก CRL',
    icon: AccountBalanceWalletRoundedIcon,
    gradient: 'linear-gradient(135deg, #14b8a6 0%, #0891b2 100%)',
  },
  {
    key: 'refund',
    label: 'รอรับคืน',
    icon: AssignmentReturnRoundedIcon,
    gradient: 'linear-gradient(135deg, #0288d1 0%, #2e7d32 100%)',
  },
];

function ExpensesList({ title, subtitle }) {
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);
  const [popupProjectId, setPopupProjectId] = useState(null);
  const [tabCounts, setTabCounts] = useState({});
  // จำนวนงวดรายการเบิกประจำที่ถึงช่วงเตือนแล้วยังไม่ได้สร้าง (ต่างจาก tabCounts.recurring ที่นับรายการที่สร้างแล้ว)
  const [recurringDueCount, setRecurringDueCount] = useState(0);

  const bumpRefresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  // ตอน HR แก้ตารางเงินเดือนอยู่ (หน้า HrReportV2) debounce sync ค่าแรงยิง expenses:created/updated/deleted
  // ออกมาได้ทุก 2.5 วิ (broadcast ทุกคนตาม convention เดิม ไม่มี room) — ถ้าเรียก bumpRefresh ตรงๆ ทุก event
  // ใครก็ตามที่เปิดหน้านี้ค้างไว้ระหว่างนั้นจะโดน refetch ทั้ง 6 tab รัวๆ ตลอดเวลาที่ HR กำลังพิมพ์ debounce
  // เองอีกชั้นให้เหลือ refetch ห่างกันอย่างน้อย ๆ ตามช่วงเวลานี้แทน
  const bumpRefreshTimerRef = useRef(null);
  const debouncedBumpRefresh = useCallback(() => {
    clearTimeout(bumpRefreshTimerRef.current);
    bumpRefreshTimerRef.current = setTimeout(bumpRefresh, 3000);
  }, [bumpRefresh]);
  useEffect(() => () => clearTimeout(bumpRefreshTimerRef.current), []);

  // me/info ใช้ร่วมกันทุก tab (สิทธิ์อนุมัติ, รูปแบบการอนุมัติ) — โหลดครั้งเดียวตรงนี้
  useEffect(() => {
    dispatch(actions.meGet());
    dispatch(actions.getInformation({}));
  }, [dispatch]);

  // จำนวนรายการของแต่ละ tab — ยิงขอแค่ size=1 (เอาแค่ total จาก pagination facet ไม่เอาข้อมูลจริง) แยกอิสระจาก fetch หลักของแต่ละ tab
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        TABS.map(async (t) => {
          try {
            // แท็บ "รอรับคืน" ไม่ได้มาจาก /expenses?tab= (รายการคืนถูกซ่อนจาก endpoint นั้น) — นับรายการที่ยังรอคืน
            if (t.key === 'refund') {
              const { data } = await api.get(`${process.env.REACT_APP_API_URL}/expenses/refunds?status=PENDING`);
              return [t.key, Array.isArray(data) ? data.length : 0];
            }
            // แท็บ "รายการเบิกประจำ" โชว์รายการเบิกจริงที่ isRegular:true — backend ใช้ tab key "regular"
            // (ไม่ใช่ "recurring") ดู getTabMatch ใน expenses.service.js
            const backendTabKey = t.key === 'recurring' ? 'regular' : t.key;
            const { data } = await api.get(
              `${process.env.REACT_APP_API_URL}/expenses?tab=${backendTabKey}&size=1&page=1`,
            );
            return [t.key, data?.total ?? 0];
          } catch (error) {
            console.error(error);
            return [t.key, null];
          }
        }),
      );
      if (!cancelled) setTabCounts(Object.fromEntries(entries));
      try {
        const { data } = await api.get(`${process.env.REACT_APP_API_URL}/expenses-recurring/due-count`);
        if (!cancelled) setRecurringDueCount(data?.count || 0);
      } catch (error) {
        console.error(error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  // Socket.IO real-time — เฉพาะ tab ค้างค้าง/อนาคต/Archive/ทั้งหมด (tab วันนี้จัดการ realtime ของตัวเองอยู่แล้ว)
  const socket = useSocket();
  useEffect(() => {
    if (!socket) return;
    socket.on('expenses:created', debouncedBumpRefresh);
    socket.on('expenses:updated', debouncedBumpRefresh);
    socket.on('expenses:deleted', debouncedBumpRefresh);
    socket.on('payout:created', debouncedBumpRefresh);

    return () => {
      socket.off('expenses:created', debouncedBumpRefresh);
      socket.off('expenses:updated', debouncedBumpRefresh);
      socket.off('expenses:deleted', debouncedBumpRefresh);
      socket.off('payout:created', debouncedBumpRefresh);
    };
  }, [socket, debouncedBumpRefresh]);

  return (
    <div>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 0.75,
            p: 0.75,
            borderRadius: '18px',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0,0,0,0.35)'
                : '0 4px 20px rgba(15,23,42,0.06)',
          }}
        >
          {TABS.map((t, idx) => {
            const selected = activeTab === idx;
            const Icon = t.icon;
            return (
              <Box
                key={t.key}
                onClick={() => setActiveTab(idx)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.9,
                  px: 2,
                  py: 1,
                  borderRadius: '14px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                  transition:
                    'transform 0.2s ease, background-color 0.2s ease, box-shadow 0.25s ease',
                  transform: selected ? 'translateY(-1px)' : 'none',
                  background: selected ? t.gradient : 'transparent',
                  boxShadow: selected
                    ? '0 6px 16px -4px rgba(0,0,0,0.35)'
                    : 'none',
                  '&:hover': {
                    background: selected
                      ? t.gradient
                      : (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.06)'
                            : 'rgba(15,23,42,0.04)',
                  },
                }}
              >
                <Icon
                  sx={{
                    fontSize: 19,
                    color: selected ? '#fff' : 'text.secondary',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: selected ? 700 : 500,
                    color: selected ? '#fff' : 'text.secondary',
                  }}
                >
                  {t.label}
                </Typography>
                {typeof tabCounts[t.key] === 'number' && (
                  <Box
                    sx={{
                      minWidth: 20,
                      height: 20,
                      px: 0.7,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '999px',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      lineHeight: 1,
                      bgcolor: selected
                        ? 'rgba(255,255,255,0.28)'
                        : (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'rgba(255,255,255,0.12)'
                              : 'rgba(15,23,42,0.07)',
                      color: selected ? '#fff' : 'text.secondary',
                    }}
                  >
                    {tabCounts[t.key]}
                  </Box>
                )}
                {t.key === 'recurring' && recurringDueCount > 0 && (
                  <Tooltip title={`มี ${recurringDueCount} งวดที่ต้องสร้างรายการเบิก (ไปที่เมนู "รายการเบิกประจำ")`}>
                    <Box
                      sx={{
                        minWidth: 20,
                        height: 20,
                        px: 0.7,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '999px',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        lineHeight: 1,
                        bgcolor: '#f97316',
                        color: '#fff',
                      }}
                    >
                      เตือน {recurringDueCount}
                    </Box>
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Paper>
      </Box>

      {activeTab === 0 && (
        <ExpensesTabToday title={title} subtitle={subtitle} />
      )}

      {TABS.map((t, index) => {
        if (index === 0 || activeTab !== index) return null;
        if (t.key === 'labor') {
          return (
            <ExpensesTabLabor
              key={t.key}
              refreshTick={refreshTick}
              onDoubleClickProject={(pid) => setPopupProjectId(pid)}
            />
          );
        }
        const TabComponent = TAB_COMPONENTS[t.key];
        return (
          <TabComponent
            key={t.key}
            refreshTick={refreshTick}
            onDoubleClickProject={(pid) => setPopupProjectId(pid)}
          />
        );
      })}

      <Dialog
        open={!!popupProjectId}
        onClose={() => setPopupProjectId(null)}
        maxWidth="xl"
        fullWidth
        sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
        PaperProps={{
          sx: { mt: '16px', maxHeight: 'calc(100vh - 32px)', minWidth: '50vw' },
        }}
        hideBackdrop
      >
        <DialogContent sx={{ p: 0 }}>
          {popupProjectId && (
            <ProjectPopup
              propId={popupProjectId}
              onClose={() => setPopupProjectId(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ExpensesList;
